import { Button, SimpleAlert, Spinner, TextInput } from '@cimplico/components';
import Papa from 'papaparse';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CsvFileRowError from '../../components/error-alerts/CsvFileRowError';
import TemplateUploadDropzone from '../../components/file-upload/TemplateUploadDropzone';
import TemplateUploadPreview from '../../components/file-upload/TemplateUploadPreview';
import VerticalRadioInput from '../../components/radio-input/VerticalRadioInput';
import { toastNotification } from '../../components/toast/Toast';
import { useFirmContext } from '../../providers/FirmProvider';
import { useCreateChecklist } from '../../queries/use-create-checklist';
import { useCreateChecklistItems } from '../../queries/use-create-checklist-items';
import { ChecklistCsv } from '../../types/checklist-csv';
import { parseTitle } from '../../utilities/formatting';
import { validateData } from './FileValidationUtils';
import UpgradePrompt from '../../components/upgrade-prompt/UpgradePrompt';
import { SubscriptionTier } from '../../types/firm';

function NewChecklistForm(): JSX.Element {
    const [name, setName] = useState<{ value?: string; errorMessage?: string }>();
    const [source, setSource] = useState<string | undefined>('manual');
    const [file, setFile] = useState<string | undefined>();
    const [data, setData] = useState<ChecklistCsv[] | undefined>();
    const [fileError, setFileError] = useState<string | undefined>(undefined);
    const [uploading, setUploading] = useState(false);
    const [errorMessages, setErrorMessages] = useState<{ [rowNumber: string]: string[] }>({});
    const [promptUpgrade, setPromptUpgrade] = useState(false);

    const navigate = useNavigate();
    const firm = useFirmContext();
    const { shortId, subscription, subscriptionVersion } = firm;
    const quota = subscriptionVersion === 1 ? firm.quota : null;
    const quotaReached = quota === null || quota.checklistLimit === null ? false : quota.checklistCount >= quota!.checklistLimit;

    const tier = subscription?.tier;

    const createChecklistQuery = useCreateChecklist();
    const createChecklistItemsQuery = useCreateChecklistItems();

    const columnFields = ['Name *', 'Description', 'Category', 'Report Code *'];

    function upload(files: File[]): void {
        setUploading(true);
        if (files[0].type !== 'text/csv') {
            setFileError('Invalid file type. File must be .csv extension.');
        }
        Papa.parse(files[0], {
            header: true,
            complete: function (results) {
                setUploading(false);
                setFile(files[0].name);
                setData(results.data as ChecklistCsv[]);
            },
        });
    }

    function deleteFile(): void {
        setFile(undefined);
        setData(undefined);
        setFileError(undefined);
        setErrorMessages({});
    }

    function handleSourceChange(source: string): void {
        if (source === 'manual') {
            deleteFile();
        }
        setSource(source);
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        if (quotaReached) {
            setPromptUpgrade(true);
            return;
        }

        if (!name?.value) {
            setName({ errorMessage: 'A checklist name must be provided.' });
        } else if (source === 'manual') {
            createChecklistQuery.mutate(
                { name: name.value, categories: [] },
                {
                    onSuccess: (res) => {
                        navigate(`/${shortId}/templates/checklists/${res.checklist.id}`);
                        toastNotification('success', 'New checklist template created!');
                    },
                    onError: (err) => {
                        const errorMessage = err.response?.data.message;
                        setName((prev) => ({ ...prev, errorMessage: err.response?.data.message }));
                        toastNotification('error', 'Unable to create new checklist!');
                    },
                }
            );
        } else if (source === 'import') {
            if (!data) {
                setFileError('A file must be uploaded.');
            } else if (data.length === 0) {
                setFileError('File must contains at least 2 rows of data.');
            } else if (JSON.stringify(Object.getOwnPropertyNames(data[0])) !== JSON.stringify(columnFields)) {
                setFileError('Invalid file. The template should be used.');
            } else {
                const result = validateData(data);
                const checklistItemRows = result[1];
                if (Object.keys(result[0]).length > 0) {
                    setErrorMessages(result[0]);
                } else {
                    const categoryNames = checklistItemRows
                        .filter((row) => row.category !== undefined)
                        .map((row) => row.category) as string[];
                    const uniqueCategoryNames: string[] = [];
                    categoryNames.forEach(function (name) {
                        const lowerCaseCategoryNames = uniqueCategoryNames.map((e) => {
                            return e.toLowerCase();
                        });
                        if (!(uniqueCategoryNames.includes(name) || lowerCaseCategoryNames.includes(name.toLowerCase()))) {
                            uniqueCategoryNames.push(name);
                        }
                    });

                    const categories: { name: string; reference: string }[] = [];
                    uniqueCategoryNames.forEach((name) => {
                        categories.push({ name, reference: parseTitle(name, 3) });
                    });

                    createChecklistQuery.mutate(
                        { name: name.value, categories },
                        {
                            onSuccess: (res) => {
                                for (const row of checklistItemRows) {
                                    for (const c of res.checklist.categories) {
                                        if (row.category && row.category.toLocaleLowerCase() === c.name.toLocaleLowerCase()) {
                                            const index = checklistItemRows.indexOf(row);
                                            checklistItemRows[index].category = c.id;
                                        }
                                    }
                                }

                                createChecklistItemsQuery.mutate(
                                    {
                                        checklistId: res.checklist.id,
                                        checklistItems: checklistItemRows.map((pr) => ({
                                            categoryId: pr.category,
                                            name: pr.name,
                                            description: pr.description,
                                            reportCodes: pr.reportCodes,
                                        })),
                                    },
                                    {
                                        onSuccess: () => {
                                            navigate(`/${shortId}/templates/checklists/${res.checklist.id}`);
                                            toastNotification('success', 'New checklist items created!');
                                        },
                                        onError: (err) => {
                                            console.log(err.response?.data.message);
                                            toastNotification('error', 'Unable to create new checklist items!');
                                        },
                                    }
                                );
                            },
                            onError: (err) => {
                                const errorMessage = err.response?.data.message;
                                if (errorMessage.includes('duplicate key value')) {
                                    setName((prev) => ({ ...prev, errorMessage: 'A checklist with this name already exists.' }));
                                } else {
                                    setName((prev) => ({ ...prev, errorMessage: err.response?.data.message }));
                                }
                                toastNotification('error', 'Unable to create new checklist!');
                            },
                        }
                    );
                }
            }
        }
    }

    function handleCancel(): void {
        navigate(`/${shortId}/templates/checklists`);
    }

    return (
        <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
            {uploading && (
                <div className="absolute top-0 left-0 h-full w-full bg-white/60 z-50 flex items-center justify-center">
                    <Spinner />
                </div>
            )}

            {promptUpgrade && (
                <UpgradePrompt
                    onClose={(): void => setPromptUpgrade(false)}
                    targetTier={tier === SubscriptionTier.BASIC ? SubscriptionTier.ESSENTIAL : SubscriptionTier.PROFESSIONAL}
                    quota
                />
            )}

            {/* PRINT ERROR MESSAGES FOR EVERY ROWS */}
            {Object.keys(errorMessages).length > 0 && <CsvFileRowError errorMessages={errorMessages} />}

            {fileError && (
                <div className="mb-4">
                    <SimpleAlert type="error" title={fileError} />
                </div>
            )}

            {/* CHECKLIST NAME */}
            <TextInput
                title="Checklist Name"
                placeholder="e.g. Firm Checklist"
                onChange={(value): void => {
                    setName({ value, errorMessage: '' });
                }}
                errorMessage={name?.errorMessage}
                isDisabled={createChecklistQuery.isLoading || createChecklistItemsQuery.isLoading}
            />

            {/* MANUAL OR IMPORT */}
            <VerticalRadioInput
                title="Checklist Source"
                description="Where would you like your checklist to come from?"
                optionOneLabel="Manual"
                optionTwoLabel="Import"
                value={source}
                onValueChange={handleSourceChange}
            />

            {/* FILE UPLOAD SECTION */}
            {source === 'import' && !file && (
                <TemplateUploadDropzone
                    columns={['"Name *","Description","Category","Report Code *"']}
                    fileName="checklist.csv"
                    onUpload={upload}
                />
            )}

            {/* SHOW UPLOADED FILE NAME */}
            {file && <TemplateUploadPreview fileName={file} onDelete={deleteFile} />}

            <div className="flex justify-end gap-4">
                <Button type="hollow" label="Cancel" onClick={handleCancel} />
                <Button
                    type="primary"
                    label="Create Checklist"
                    disabled={createChecklistQuery.isLoading || createChecklistItemsQuery.isLoading}
                    isLoading={createChecklistQuery.isLoading || createChecklistItemsQuery.isLoading}
                    isSubmit
                />
            </div>
        </form>
    );
}

export default NewChecklistForm;
